import React, { lazy, Suspense } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { Box, Flex, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { isInsufficientContextError } from '../../../common/isInsufficientContextError';
import { AUDIO_CONTAINER_MODES } from '../../../types/audioContainerMode';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';

const WaveformAnimation = lazy(() =>
	import('./WaveformAnimation').then((mod) => ({ default: mod.WaveformAnimation })),
);

import { PlayingAudioEmojiIcon } from './AudioIconStack';
import { AudioTimeIndicator } from './AudioTimeIndicator';
import { MiniPauseButton } from './controls/MiniPauseButton';
import { MiniPlayButton } from './controls/MiniPlayButton';

const i18n = defineMessages({
	errorHeading: {
		id: 'audio.minified-audio-player.error-heading',
		defaultMessage: 'Error loading audio',
		description: 'The error message displayed when the audio fails to load',
	},
	insufficientContextErrorHeading: {
		id: 'audio.minified-audio-player.insufficient-context-error-heading',
		defaultMessage: 'Not enough info to create a briefing',
		description: 'The error message displayed when there is not enough content to generate audio',
	},
});

const styles = cssMap({
	container: {
		position: 'relative',
		width: '100%',
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		backgroundColor: token('elevation.surface'),
	},
	elevated: {
		width: `100%`,
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	overlayButton: {
		position: 'absolute',
		inset: 0,
		width: '100%',
		height: '100%',
		padding: 0,
		borderRadius: token('radius.large'),
		zIndex: 0,
		backgroundColor: token('elevation.surface'),
	},
	content: {
		position: 'relative',
		zIndex: 1,
	},
	warningIconContainer: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
	contentArea: {
		flex: '1 1 0%',
	},
	emojiIconContainer: {
		transform: 'scale(1.6)',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

const dynamicStyles = {
	minWidth: `${AUDIO_CONTAINER_MODES.MINIFIED.width}px`,
};

type MinifiedAudioPlayerProps = {
	byline: string;
	title: string;
	elevated?: boolean;
	onClick?: () => void;
};

export const MinifiedAudioPlayer = ({
	title,
	byline,
	elevated,
	onClick,
}: MinifiedAudioPlayerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { state } = useAudioPlayerStreamingStore();
	const { isPaused, isPlaying } = useAudioPlayerStore();

	const hasFetched = state.state !== AudioStreamingState.UNINITIALIZED;
	const isErrorState = state.state === AudioStreamingState.ERROR;

	if (isErrorState) {
		const hasInsufficientContext = isInsufficientContextError(state.error);

		return (
			<Box style={dynamicStyles}>
				<Box xcss={cx(styles.container, elevated && styles.elevated)}>
					{onClick && (
						<Pressable
							onClick={onClick}
							xcss={styles.overlayButton}
							testId="minified-audio-player-pressable"
						/>
					)}
					<Inline alignBlock="center" space="space.100" xcss={styles.content}>
						<Box xcss={styles.warningIconContainer}>
							<StatusWarningIcon color={token('color.icon.warning')} label="" />
						</Box>
						<Heading size="xsmall">
							{formatMessage(
								hasInsufficientContext ? i18n.insufficientContextErrorHeading : i18n.errorHeading,
							)}
						</Heading>
					</Inline>
				</Box>
			</Box>
		);
	}

	return (
		<Box style={dynamicStyles}>
			<Flex
				gap="space.100"
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				xcss={cx(styles.container, elevated && styles.elevated)}
			>
				{onClick && (
					<Pressable
						onClick={onClick}
						xcss={styles.overlayButton}
						testId="minified-audio-player-pressable"
					/>
				)}
				<Flex
					gap="space.100"
					direction="row"
					alignItems="center"
					justifyContent="start"
					xcss={styles.content}
				>
					<Box xcss={styles.emojiIconContainer}>
						<PlayingAudioEmojiIcon />
					</Box>
					<Tooltip content={title}>
						<Box xcss={styles.contentArea}>
							<Stack space="space.025">
								<Text maxLines={1} align="start">
									<Heading size="xxsmall">{title}</Heading>
								</Text>
								{hasFetched ? (
									<AudioTimeIndicator />
								) : (
									<Text size="small" color="color.text.subtlest" maxLines={1} align="start">
										{byline}
									</Text>
								)}
							</Stack>
						</Box>
					</Tooltip>
				</Flex>
				{hasFetched && (isPlaying || isPaused) && (
					<Inline space="space.100" alignBlock="center" xcss={styles.content}>
						{isPaused ? (
							<MiniPlayButton />
						) : (
							<>
								<Suspense fallback={null}>
									<WaveformAnimation />
								</Suspense>
								<MiniPauseButton />
							</>
						)}
					</Inline>
				)}
			</Flex>
		</Box>
	);
};
